/* Multi-select. Design notes live in MultiSelect.css.

   Wraps a .dropdown, so open/close, Escape and outside-click all come from
   Dropdown.ts. This file only adds the checkbox list and the summary of what's
   ticked.

     <div class="dropdown multiselect" data-summary="badge">
       <button type="button" class="dropdown-trigger">Faciliteter</button>
       <div class="dropdown-panel" hidden>
         <div class="multiselect-group" data-visible="5">
           <p class="multiselect-group-title">Fællesfaciliteter</p>
           <label class="multiselect-option">
             <input type="checkbox" name="facilities" value="elevator"> Elevator
           </label>
           …
         </div>
       </div>
     </div>

   data-summary picks how the selection reads back:
     "badge" — a count on the top edge of the control (Figma node 3435:17556)
     "chips" — one removable pill per selection (Figma node 3435:17545)
   Anything else, and the checkboxes speak for themselves.

   Chips land in a .multiselect-chips element if the markup provides one — put
   it inside an .input-holder to get Figma's inline-in-the-field look — and
   otherwise in one created just above the trigger.

   data-visible on a group shows that many rows and appends a "Se alle" that
   reveals the rest. A row that is already ticked is never hidden, so the
   summary can't disagree with the list.

   The checkboxes are the value: same `name`, and they post as an array with no
   hidden field to keep in step. Picking one fires a bubbling
   `multiselect:change` with { values, labels }. */

const OPTION = '.multiselect-option';

function labelOf(box: HTMLInputElement) {
  const row = box.closest<HTMLElement>(OPTION);
  return row?.dataset.label ?? row?.textContent?.trim() ?? box.value;
}

function uncheck(box: HTMLInputElement) {
  box.checked = false;
  // `change` rather than a direct re-render: anything else listening on the
  // form hears it too, and our own listener does the redraw.
  box.dispatchEvent(new Event('change', { bubbles: true }));
}

/* Shows `data-visible` rows and hands back a "Se alle" that reveals the rest. */
function collapse(group: HTMLElement, showAllLabel: string) {
  const limit = Number(group.dataset.visible);
  const options = Array.from(group.querySelectorAll<HTMLElement>(OPTION));
  if (!limit || options.length <= limit) return;

  const surplus = options.slice(limit);
  surplus.forEach((option) => {
    option.hidden = !option.querySelector<HTMLInputElement>('input')?.checked;
  });

  const showAll = document.createElement('button');
  showAll.type = 'button';
  showAll.className = 'multiselect-show-all';
  showAll.textContent = showAllLabel;
  showAll.addEventListener('click', () => {
    surplus.forEach((option) => (option.hidden = false));
    // Figma draws no collapsed-again state, so there's no label to put here.
    showAll.remove();
  });
  group.append(showAll);
}

function wire(root: HTMLElement) {
  const panel = root.querySelector<HTMLElement>('.dropdown-panel');
  const trigger = root.querySelector<HTMLElement>('.dropdown-trigger');
  if (!panel || !trigger) return;

  const summary = root.dataset.summary;
  const boxes = () => Array.from(panel.querySelectorAll<HTMLInputElement>(`${OPTION} input[type="checkbox"]`));

  root
    .querySelectorAll<HTMLElement>('.multiselect-group')
    .forEach((group) => collapse(group, root.dataset.showAllLabel ?? 'Se alle'));

  let badgeCount: Text | undefined;
  let badge: HTMLElement | undefined;
  let chips: HTMLElement | undefined;

  if (summary === 'badge') {
    badge = document.createElement('span');
    badge.className = 'multiselect-badge';
    badgeCount = document.createTextNode('0');
    // A bare number reads as nothing on its own, so the badge carries the noun
    // for screen readers. It sits right before the trigger in reading order.
    const suffix = document.createElement('span');
    suffix.className = 'sr-only';
    suffix.textContent = ` ${root.dataset.selectedLabel ?? 'valgt'}`;
    badge.append(badgeCount, suffix);
    trigger.before(badge);
  }

  if (summary === 'chips') {
    chips = root.querySelector<HTMLElement>('.multiselect-chips') ?? undefined;
    if (!chips) {
      chips = document.createElement('span');
      chips.className = 'multiselect-chips';
      trigger.before(chips);
    }
  }

  const removeLabel = root.dataset.removeLabel ?? 'Fjern';

  const render = () => {
    const checked = boxes().filter((box) => box.checked);

    if (badge && badgeCount) {
      badgeCount.data = String(checked.length);
      badge.hidden = checked.length === 0;
    }

    if (chips) {
      chips.replaceChildren(
        ...checked.map((box) => {
          const label = labelOf(box);
          const chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'multiselect-chip';
          chip.textContent = label;
          chip.setAttribute('aria-label', `${removeLabel} ${label}`);
          chip.addEventListener('click', () => uncheck(box));
          return chip;
        }),
      );
    }

    return checked;
  };

  root.addEventListener('change', (event) => {
    if (!(event.target as HTMLElement).closest(OPTION)) return;
    const checked = render();
    root.dispatchEvent(
      new CustomEvent('multiselect:change', {
        bubbles: true,
        detail: { values: checked.map((box) => box.value), labels: checked.map(labelOf) },
      }),
    );
  });

  render();
}

export default function initMultiSelect(scope: ParentNode = document) {
  scope.querySelectorAll<HTMLElement>('.multiselect').forEach(wire);
}
